import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { commentService } from "@/services/commentService";
import { toast } from "sonner";

export function useComments(priorityPid: string, page: number = 1, limit: number = 20, enabled: boolean = true) {
  return useQuery({
    queryKey: ["comments", priorityPid, page, limit],
    queryFn: () => commentService.getComments(priorityPid, page, limit),
    enabled: enabled && !!priorityPid,
  });
}

export function useAddComment(priorityPid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (content: string) => commentService.addComment(priorityPid, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments", priorityPid] });
    },
    onError: () => {
      toast.error("Failed to add comment. Please try again.");
    },
  });
}

export function useUpdateComment(priorityPid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentPid, content }: { commentPid: string; content: string }) =>
      commentService.updateComment(commentPid, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments", priorityPid] });
    },
    onError: () => {
      toast.error("Failed to update comment. Please try again.");
    },
  });
}

export function useDeleteComment(priorityPid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentPid: string) => commentService.deleteComment(commentPid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments", priorityPid] });
      toast.success("Comment deleted");
    },
    onError: () => {
      toast.error("Failed to delete comment. Please try again.");
    },
  });
}
